'use client'

import { CheckIcon, CopyIcon, Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import StepUpVerificationFields, {
  StepUpFieldValues,
} from '@/components/dialog/settings/step-up-verification-fields'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OptionSwitch } from '@/components/ui/option-switch'
import { DatePicker } from '@/components/widget/date-picker'
import { useAuth } from '@/features/auth/hooks'
import {
  API_KEY_LABEL_MAX,
  API_KEY_SCOPES,
  ApiKeyScope,
  EXPIRY_PRESETS,
  ExpiryPreset,
  resolveExpiry,
} from '@/features/settings/api-keys/lib/api-keys'
import { copyText } from '@/lib/clipboard'
import { classifyStepUpError, getStepUpMode, pickStepUpVerification } from '@/lib/step-up'
import { cn } from '@/lib/utils'
import { useCreateApiKey } from '@/service/api-key.hook'
import { SensitiveAction } from '@/types/generated/common/auth'
import type { ErrorResponse } from '@/types/service'

type Step = 'form' | 'verify' | 'reveal'

// The custom expiry is kept as the "YYYY-MM-DD" a date input produced (resolveExpiry reads it).
const pad2 = (n: number) => String(n).padStart(2, '0')
const formatLocalDay = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
const parseLocalDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

type VerifyFormData = {
  currentPassword: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
} & StepUpFieldValues

const EMPTY_VERIFY: VerifyFormData = {
  currentPassword: '',
  totpCode: '',
  backupCode: '',
  emailCode: '',
}

/**
 * Three screens in one dialog: what the key may do → step-up (same flow as Change password) →
 * the show-once key. The full key lives only in this component's state while the reveal screen
 * is up, and is dropped when the dialog closes — never a store, the query cache or a toast.
 */
export default function CreateApiKeyDialog({
  open,
  onOpenChange,
  is2faEnabled,
  is2faLoading,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  is2faEnabled: boolean
  /** The 2FA status hasn't loaded yet — the factor can't be picked, so the step-up waits. */
  is2faLoading: boolean
}) {
  const t = useTranslations('settings')
  const { hasPassword } = useAuth()
  const { create, isPending } = useCreateApiKey()

  const [step, setStep] = useState<Step>('form')
  const [label, setLabel] = useState('')
  const [scopes, setScopes] = useState<ApiKeyScope[]>([])
  const [executeAcknowledged, setExecuteAcknowledged] = useState(false)
  const [expiryPreset, setExpiryPreset] = useState<ExpiryPreset>('never')
  const [customDate, setCustomDate] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  // The factor starts from the 2FA state we already hold; the server can correct it
  // (TOTP_REQUIRED / EMAIL_CODE_REQUIRED) without a separate probe request.
  // Derived, not seeded into state: this dialog mounts with the page, often before the 2FA
  // status has loaded, and a seeded value would lock in "no TOTP" and ask for an email code.
  const [factorOverride, setFactorOverride] = useState<'totp' | 'email' | null>(null)
  const totpFactor = factorOverride ? factorOverride === 'totp' : is2faEnabled
  const [revealedKey, setRevealedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const form = useForm<VerifyFormData>({ defaultValues: EMPTY_VERIFY })

  const trimmedLabel = label.trim()
  const expiry = resolveExpiry(expiryPreset, customDate)
  const needsExecuteAck = scopes.includes('execute') && !executeAcknowledged
  const formReady =
    trimmedLabel.length > 0 &&
    trimmedLabel.length <= API_KEY_LABEL_MAX &&
    expiry !== null &&
    !needsExecuteAck &&
    !is2faLoading

  const mode = getStepUpMode(totpFactor)
  const verificationReady =
    (!hasPassword || Boolean(form.watch('currentPassword'))) &&
    (mode === 'totp'
      ? Boolean(form.watch('totpCode')) || Boolean(form.watch('backupCode'))
      : form.watch('emailCode')?.length === 6)

  const reset = () => {
    setStep('form')
    setLabel('')
    setScopes([])
    setExecuteAcknowledged(false)
    setExpiryPreset('never')
    setCustomDate('')
    setFormError(null)
    setFactorOverride(null)
    setRevealedKey(null)
    setCopied(false)
    form.reset(EMPTY_VERIFY)
  }

  const handleOpenChange = (next: boolean) => {
    // The reveal screen closes only through "I've copied it".
    if (!next && step === 'reveal') return
    if (!next) reset()
    onOpenChange(next)
  }

  const closeAfterReveal = () => {
    reset()
    onOpenChange(false)
  }

  const toggleScope = (scope: ApiKeyScope, checked: boolean) => {
    setScopes((prev) => (checked ? [...prev, scope] : prev.filter((s) => s !== scope)))
    if (scope === 'execute' && !checked) setExecuteAcknowledged(false)
  }

  const onSubmit = async (data: VerifyFormData) => {
    setFormError(null)
    form.clearErrors()
    // Re-resolve against the clock now: a preset counts from the moment of the request.
    const expiresAt = resolveExpiry(expiryPreset, customDate)
    if (expiresAt === null) {
      setStep('form')
      return
    }
    try {
      const apiKey = await create({
        label: trimmedLabel,
        scopes: API_KEY_SCOPES.filter((scope) => scopes.includes(scope)),
        expiresAt: expiresAt === undefined ? undefined : String(expiresAt),
        currentPassword: data.currentPassword,
        ...pickStepUpVerification(data),
      })
      form.reset(EMPTY_VERIFY)
      setRevealedKey(apiKey)
      setStep('reveal')
    } catch (error) {
      // Never retried here: every failed attempt counts toward the step-up throttle.
      const failure = classifyStepUpError((error as ErrorResponse)?.message)
      switch (failure.kind) {
        case 'password':
          form.setError('currentPassword', { message: t('apiKeys.errors.password') })
          break
        case 'totp-required':
          setFactorOverride('totp')
          setFormError(t('apiKeys.errors.totpRequired'))
          break
        case 'email-code-required':
          // Switch to the email field; the user sends the code themselves.
          setFactorOverride('email')
          setFormError(t('apiKeys.errors.emailCodeRequired'))
          break
        case 'totp-invalid':
          form.setError(data.backupCode && !data.totpCode ? 'backupCode' : 'totpCode', {
            message: t('apiKeys.errors.totpInvalid'),
          })
          break
        case 'email-code-invalid':
          form.setError('emailCode', { message: t('apiKeys.errors.emailCodeInvalid') })
          break
        default:
          setFormError(
            (failure.kind === 'other' && failure.message) || t('apiKeys.errors.createFailed')
          )
      }
    }
  }

  const copyKey = async () => {
    if (!revealedKey) return
    setCopied(await copyText(revealedKey))
  }

  const isReveal = step === 'reveal'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        // Hide the built-in close X on the reveal screen (it's DialogContent's last child).
        className={cn(
          'max-h-[calc(100dvh-32px)] overflow-y-auto',
          isReveal && '[&>button:last-child]:hidden'
        )}
        onInteractOutside={(event) => isReveal && event.preventDefault()}
        onEscapeKeyDown={(event) => isReveal && event.preventDefault()}
      >
        <DialogTitle className="flex flex-col gap-2">
          {isReveal ? t('apiKeys.reveal.title') : t('apiKeys.create.title')}
          <p className="text-foreground/60 text-sm font-normal tracking-tight">
            {step === 'form'
              ? t('apiKeys.create.description')
              : step === 'verify'
                ? t('apiKeys.create.verifyDescription')
                : t('apiKeys.reveal.warning')}
          </p>
        </DialogTitle>

        {step === 'form' && (
          <form
            className="mt-4 flex w-full flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault()
              if (formReady) setStep('verify')
            }}
          >
            <div className="flex flex-col gap-1">
              <Label className="text-sm tracking-tight">{t('apiKeys.create.label')}</Label>
              <Input
                value={label}
                maxLength={API_KEY_LABEL_MAX}
                placeholder={t('apiKeys.create.labelPlaceholder')}
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm tracking-tight">{t('apiKeys.create.permissions')}</Label>
              <p className="text-foreground/60 text-xs tracking-tight">
                {t('apiKeys.create.permissionsDescription')}
              </p>
              {/* Market data comes with every key; shown ticked so "nothing else ticked" reads
                  as a market-data key, not as a form left blank. */}
              <label className="flex items-start gap-2">
                <Checkbox className="mt-0.5" checked disabled />
                <span className="flex flex-col">
                  <span className="text-sm tracking-tight">{t('apiKeys.scopes.marketData')}</span>
                  <span className="text-foreground/50 text-xs tracking-tight">
                    {t('apiKeys.scopes.marketDataHint')}
                  </span>
                </span>
              </label>
              {API_KEY_SCOPES.map((scope) => (
                <label key={scope} className="flex cursor-pointer items-start gap-2">
                  <Checkbox
                    className="mt-0.5"
                    checked={scopes.includes(scope)}
                    onCheckedChange={(checked) => toggleScope(scope, checked === true)}
                  />
                  <span className="flex flex-col">
                    <span className="text-sm tracking-tight">{t(`apiKeys.scopes.${scope}`)}</span>
                    <span className="text-foreground/50 font-mono text-xs">{scope}</span>
                  </span>
                </label>
              ))}
              {scopes.includes('execute') && (
                <label className="border-foreground/15 flex cursor-pointer items-start gap-2 border p-3">
                  <Checkbox
                    className="mt-0.5"
                    checked={executeAcknowledged}
                    onCheckedChange={(checked) => setExecuteAcknowledged(checked === true)}
                  />
                  <span className="text-sm tracking-tight">
                    {t('apiKeys.create.executeConfirm')}
                  </span>
                </label>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-sm tracking-tight">{t('apiKeys.create.expiry')}</Label>
              <OptionSwitch
                options={EXPIRY_PRESETS}
                value={expiryPreset}
                onChange={setExpiryPreset}
                labels={{
                  never: t('apiKeys.expiry.never'),
                  '30d': t('apiKeys.expiry.30d'),
                  '90d': t('apiKeys.expiry.90d'),
                  '1y': t('apiKeys.expiry.1y'),
                  custom: t('apiKeys.expiry.custom'),
                }}
              />
              {expiryPreset === 'custom' && (
                <div className="flex flex-col gap-1">
                  <DatePicker
                    size="default"
                    selected={customDate ? parseLocalDay(customDate) : undefined}
                    onSelect={(date) => setCustomDate(formatLocalDay(date))}
                    minDate={new Date()}
                    className="w-full justify-between"
                  />
                  {customDate && expiry === null && (
                    <p className="text-destructive text-sm">{t('apiKeys.errors.expiryInPast')}</p>
                  )}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={!formReady}>
              {t('apiKeys.create.continue')}
            </Button>
          </form>
        )}

        {step === 'verify' && (
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 flex w-full flex-col gap-4"
            data-1p-ignore={false}
          >
            <StepUpVerificationFields
              form={form}
              hasPassword={hasPassword}
              totpEnabled={totpFactor}
              action={SensitiveAction.SENSITIVE_ACTION_CREATE_API_KEY}
            />
            {formError && <p className="text-destructive text-sm">{formError}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                disabled={isPending}
                onClick={() => {
                  setFormError(null)
                  setStep('form')
                }}
              >
                {t('apiKeys.create.back')}
              </Button>
              <Button type="submit" className="flex-1" disabled={isPending || !verificationReady}>
                {isPending ? (
                  <Loader2Icon className="size-3.5 animate-spin" />
                ) : (
                  t('apiKeys.create.submit')
                )}
              </Button>
            </div>
          </form>
        )}

        {isReveal && revealedKey && (
          <div className="mt-4 flex w-full flex-col gap-4">
            <div className="border-foreground/15 flex items-start gap-2 border p-3">
              <code className="text-foreground min-w-0 flex-1 font-mono text-xs break-all select-all">
                {revealedKey}
              </code>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('apiKeys.reveal.copy')}
                onClick={copyKey}
              >
                {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
              </Button>
            </div>
            <p className="text-foreground/60 text-xs tracking-tight">
              {copied ? t('apiKeys.reveal.copied') : t('apiKeys.reveal.hint')}
            </p>
            <Button type="button" className="w-full" onClick={closeAfterReveal}>
              {t('apiKeys.reveal.done')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
